<template>
  <VAutocomplete
    v-model="selected"
    v-model:search="searchInput"
    :items="searchItems"
    menu-icon=""
    variant="solo-filled"
    prepend-inner-icon="search"
    :placeholder="t('Search for patients...')"
    flat
    hide-details
    @update:model-value="onSelect"
  >
    <template #item="itemArgs">
      <PatientSearchItem
        :item-props="itemArgs.props"
        :display-data="itemArgs.item.raw.displayData"
        :search-input="searchInput"
      />
    </template>
  </VAutocomplete>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useCaseStore } from '@/stores/case'
import { computed, ref } from 'vue'
import type { CaseDTO } from '@/generated'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import type { SearchItem } from '@/components/navigation/patientsearch/SearchItem'
import PatientSearchItem from '@/components/navigation/patientsearch/PatientSearchItem.vue'
import { parseISO } from 'date-fns'
import { useFormatDate } from '@/composables/useFormatDate'

const { t } = useI18n()
const router = useRouter()
const { caseList } = storeToRefs(useCaseStore())
const { formatDateOnly } = useFormatDate()

const selected = ref<string | undefined>()

const searchInput = ref<string>('')

const searchItems = computed<SearchItem<string>[]>(() =>
  caseList.value.map((caze: CaseDTO) => ({
    value: caze.id,
    title: caze.patient.dateOfBirth
      ? `${caze.externalId}, ${caze.patient.lastName}, ${caze.patient.firstName}, ${formatDateOnly(parseISO(caze.patient.dateOfBirth))}, ${caze.hospital.name}, ${caze.ward.name}`
      : `${caze.externalId}, ${caze.patient.lastName}, ${caze.patient.firstName}, ${caze.hospital.name}, ${caze.ward.name}`,
    displayData: {
      id: caze.externalId,
      name: `${caze.patient.lastName}, ${caze.patient.firstName}`,
      dateOfBirth: caze.patient.dateOfBirth
        ? formatDateOnly(parseISO(caze.patient.dateOfBirth))
        : '',
      hospital: caze.hospital.name,
      ward: caze.ward.name
    }
  }))
)

function onSelect() {
  if (!selected.value) return

  const caseId = selected.value
  selected.value = undefined
  router.push({ name: routeNames.PATIENT_DETAILS, params: { caseId } })
}
</script>
